/**
 * Inbox conversation list — shows all conversations across channels
 * for the business owner's personas.
 *
 * Supports an optional "group by sender" mode that collapses conversations
 * under accordion headers keyed by customer name / phone.
 */

import { useMemo, useCallback, useEffect, useRef } from 'react'
import { cn } from '@/utils'
import { MessageSquare, Paperclip, ChevronDown, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { Badge, Spinner, Tip } from '@/components/ui'
import type { InboxConversationSummary, ResolvedReason } from '@/types/inbox'
import { channelIcon, isGroupChannel } from './channels'
import { toPreviewText } from './inboxText'
import { rowChips } from './triageChips'
import { useSettingsStore } from '@/stores'
import { tourAnchor } from '@/components/tour/tourAnchor'

interface InboxConversationListProps {
  conversations: InboxConversationSummary[]
  selectedId: string | null
  isLoading: boolean
  onSelect: (id: string) => void
  groupBySender?: boolean
  /** Whether a "Needs review" / "Faults" filter is currently narrowing the
   * list, so an empty result can be distinguished from a genuinely empty
   * inbox. */
  filtersActive?: boolean
  total?: number
  hasMore?: boolean
  isLoadingMore?: boolean
  loadMoreError?: string | null
  onLoadMore?: () => void
}

const statusColors: Record<string, string> = {
  active: 'bg-success',
  waiting: 'bg-warning',
  human: 'bg-destructive',
  resolved: 'bg-muted-foreground',
}

// Labels for the resolved badge; `manual` deliberately renders no badge.
const RESOLVED_REASON_LABELS: Partial<Record<ResolvedReason, string>> = {
  timeout: 'auto',
  stale_cleanup: 'stale',
  superseded: 'superseded',
  triaged: 'triaged',
  // SA-759: a backload resolved a thread older than its recency window.
  backload: 'earlier mail',
}

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'now'
  if (diffMin < 60) return `${diffMin}m`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d`
}

function customerKey(conv: InboxConversationSummary): string {
  return conv.customerDisplayName || conv.customerPhone || 'Unknown'
}

/** The DOM id of a row, so the pill can scroll to it. Ids are uuids: safe in an id. */
function ConversationItem({
  conv,
  isSelected,
  onSelect,
  compact,
}: {
  conv: InboxConversationSummary
  isSelected: boolean
  onSelect: (id: string) => void
  compact?: boolean
}) {
  const ChannelIcon = channelIcon(conv.channel)
  const isEmail = conv.channel === 'email'
  // SA-946: a WhatsApp group row leads with the group's subject and names
  // who spoke last beneath it.
  const isGroup = isGroupChannel(conv.channel)
  // Email rows lead with the subject and show the sender beneath. When
  // there's no subject the sender is the primary line instead, so the
  // secondary line is left blank rather than repeating the same name.
  const emailSender = conv.customerDisplayName || 'Unknown sender'
  const primary = compact
    ? (conv.personaName || 'No persona')
    : isEmail
      ? (conv.title || emailSender)
      : isGroup
        ? (conv.title || conv.customerDisplayName || 'WhatsApp group')
        : (conv.customerDisplayName || conv.customerPhone || 'Unknown')
  const secondary = compact
    ? (conv.customerDisplayName || conv.customerPhone || 'Unknown')
    : isEmail
      ? (conv.title ? emailSender : '')
      : isGroup
        ? (conv.lastSenderName ?? '')
        : (conv.personaName || 'No persona')
  // SA-717: what triage decided, as chips (empty on a thread that was not triaged).
  const chips = rowChips(conv)

  return (
    <button
      onClick={() => onSelect(conv.id)}
      className={cn(
        // SA-1123: the sidebar's active row — a neutral wash and a primary bar; every row carries the bar's width.
        'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-border border-l-2',
        isSelected
          ? 'bg-accent border-l-primary'
          : 'border-l-transparent hover:bg-accent'
      )}
    >
      {/* Channel icon + status dot */}
      <div className="relative shrink-0 mt-0.5">
        <ChannelIcon className="w-5 h-5 text-muted-foreground" />
        <span
          className={cn(
            'absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-background',
            statusColors[conv.status] || 'bg-muted-foreground'
          )}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={cn(
            'text-sm truncate',
            conv.unreadCount > 0
              ? 'font-semibold text-foreground'
              : 'font-medium text-foreground'
          )}>
            {primary}
          </span>
          <span className="text-xs text-muted-foreground shrink-0">
            {formatTimeAgo(conv.lastMessageAt)}
          </span>
        </div>

        <div className="flex items-center gap-1 mt-0.5">
          <span className="text-xs text-muted-foreground truncate">
            {secondary}
          </span>
          {conv.attachmentCount > 0 && (
            <span
              role="img"
              className="inline-flex items-center text-[10px] text-muted-foreground shrink-0"
              aria-label={`${conv.attachmentCount} attachment${conv.attachmentCount === 1 ? '' : 's'}`}
            >
              <Paperclip className="w-3 h-3" aria-hidden="true" />
            </span>
          )}
          {conv.adAttribution && (
            <Badge variant="info-soft" className="px-1.5 py-0 text-[10px]">Ad</Badge>
          )}
          {conv.bankTransferReviewRequired && (
            <Badge variant="warning-soft" className="px-1.5 py-0 text-[10px]">Needs review</Badge>
          )}
          {conv.engineFault && (
            <Badge variant="warning-soft" className="px-1.5 py-0 text-[10px]">Fault</Badge>
          )}
          {conv.status === 'human' && (
            <Tip text="Escalated for human handling">
              <span className="inline-flex shrink-0">
                <Badge variant="destructive-soft" className="px-1.5 py-0 text-[10px]">Escalated</Badge>
              </span>
            </Tip>
          )}
          {conv.status === 'resolved' && conv.resolvedReason && conv.resolvedReason !== 'manual' && (
            <span className="text-[10px] text-muted-foreground italic">
              {RESOLVED_REASON_LABELS[conv.resolvedReason] ?? conv.resolvedReason}
            </span>
          )}
        </div>

        {chips.length > 0 && (
          <div data-testid="triage-chips" className="mt-1 flex flex-wrap items-center gap-1">
            {chips.map((chip) => (
              <Badge key={chip.key} variant={chip.variant} className="px-1.5 py-0 text-[10px]">
                {chip.label}
              </Badge>
            ))}
          </div>
        )}

        {conv.lastMessagePreview && (
          <p className={cn(
            'text-xs mt-1 truncate',
            conv.unreadCount > 0
              ? 'text-foreground'
              : 'text-muted-foreground'
          )}>
            {conv.lastMessageSenderType === 'ai' && '🤖 '}
            {conv.lastMessageSenderType === 'business' && '👤 '}
            {toPreviewText(conv.lastMessagePreview)}
          </p>
        )}
      </div>

      {/* Unread badge */}
      {conv.unreadCount > 0 && (
        <span className="shrink-0 bg-primary text-primary-foreground text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
          {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
        </span>
      )}
    </button>
  )
}

interface SenderGroup {
  key: string
  conversations: InboxConversationSummary[]
  totalUnread: number
  latestMessageAt: string | null
}

function LoadMoreSentinel({ onLoadMore }: { onLoadMore: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore()
      },
      { rootMargin: '200px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [onLoadMore])
  return <div ref={ref} className="h-px" aria-hidden="true" />
}

function ListFooter({
  loaded,
  total,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onLoadMore,
}: {
  loaded: number
  total?: number
  hasMore?: boolean
  isLoadingMore?: boolean
  loadMoreError?: string | null
  onLoadMore?: () => void
}) {
  if (total === undefined) return null
  return (
    <>
      {hasMore && !loadMoreError && !isLoadingMore && onLoadMore && (
        <LoadMoreSentinel onLoadMore={onLoadMore} />
      )}
      {loadMoreError && onLoadMore && (
        <button
          onClick={onLoadMore}
          className="w-full py-3 text-xs text-destructive hover:text-destructive/80"
        >
          Couldn't load more — tap to retry
        </button>
      )}
      <div className="py-2 flex items-center justify-center text-[10px] text-muted-foreground">
        {isLoadingMore ? <Spinner size="sm" /> : `Showing ${loaded} of ${total}`}
      </div>
    </>
  )
}

export function InboxConversationList({
  conversations,
  selectedId,
  isLoading,
  onSelect,
  groupBySender = false,
  filtersActive = false,
  total,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onLoadMore,
}: InboxConversationListProps) {
  // Persisted collapsed groups from settings store
  const { ui: { inboxCollapsedGroups }, setInboxCollapsedGroups } = useSettingsStore()
  const collapsedGroups = useMemo(() => new Set(inboxCollapsedGroups), [inboxCollapsedGroups])

  const groups = useMemo((): SenderGroup[] => {
    if (!groupBySender) return []

    const groupMap = new Map<string, InboxConversationSummary[]>()
    for (const conv of conversations) {
      const key = customerKey(conv)
      if (!groupMap.has(key)) groupMap.set(key, [])
      groupMap.get(key)!.push(conv)
    }

    const result: SenderGroup[] = []
    for (const [key, convs] of groupMap) {
      result.push({
        key,
        conversations: convs,
        totalUnread: convs.reduce((sum, c) => sum + c.unreadCount, 0),
        latestMessageAt: convs.reduce<string | null>(
          (latest, c) => {
            if (!c.lastMessageAt) return latest
            if (!latest) return c.lastMessageAt
            return c.lastMessageAt > latest ? c.lastMessageAt : latest
          },
          null,
        ),
      })
    }

    // Sort groups by most recent activity
    result.sort((a, b) => {
      if (!a.latestMessageAt && !b.latestMessageAt) return 0
      if (!a.latestMessageAt) return 1
      if (!b.latestMessageAt) return -1
      return b.latestMessageAt.localeCompare(a.latestMessageAt)
    })

    return result
  }, [conversations, groupBySender])

  const toggleGroup = useCallback((key: string) => {
    const next = new Set(collapsedGroups)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.add(key)
    }
    setInboxCollapsedGroups([...next])
  }, [collapsedGroups, setInboxCollapsedGroups])

  const allCollapsed = groupBySender && groups.length > 0 && collapsedGroups.size === groups.length

  const expandAll = useCallback(() => setInboxCollapsedGroups([]), [setInboxCollapsedGroups])

  const collapseAll = useCallback(() => {
    setInboxCollapsedGroups(groups.map((g) => g.key))
  }, [groups, setInboxCollapsedGroups])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <MessageSquare className="w-12 h-12 text-muted-foreground mb-3" />
        <p className="text-muted-foreground text-sm">
          {filtersActive ? 'No conversations match these filters' : 'No conversations yet'}
        </p>
        <p className="text-muted-foreground text-xs mt-1">
          {filtersActive
            ? 'Try turning off the "Needs review" or "Faults" filters above'
            : 'Conversations from your personas will appear here'}
        </p>
      </div>
    )
  }

  // Grouped view
  if (groupBySender) {
    return (
      <div {...tourAnchor('inbox.list.rows')} className="h-full overflow-y-auto">
        {/* Expand / Collapse all toolbar */}
        <div className="flex items-center justify-between px-4 py-1.5 border-b border-border bg-background">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            {groups.length} {groups.length === 1 ? 'customer' : 'customers'}
          </span>
          <button
            onClick={allCollapsed ? expandAll : collapseAll}
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronsUpDown className="w-3 h-3" />
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
        </div>
        {groups.map((group) => {
          const isCollapsed = collapsedGroups.has(group.key)
          const hasSelectedConv = group.conversations.some((c) => c.id === selectedId)

          return (
            <div key={group.key}>
              {/* Group header */}
              <button
                onClick={() => toggleGroup(group.key)}
                className={cn(
                  'w-full flex items-center gap-2 px-4 py-2 text-left transition-colors',
                  'bg-muted border-b border-border border-l-2',
                  'hover:bg-accent',
                  // SA-1123: the group holding the selection carries the row's primary bar, never a wash.
                  hasSelectedConv ? 'border-l-primary' : 'border-l-transparent'
                )}
              >
                {isCollapsed ? (
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                )}
                <span className={cn(
                  'text-sm truncate flex-1',
                  group.totalUnread > 0
                    ? 'font-semibold text-foreground'
                    : 'font-medium text-foreground'
                )}>
                  {group.key}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {group.conversations.length}
                </span>
                {group.totalUnread > 0 && (
                  <span className="shrink-0 bg-primary text-primary-foreground text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                    {group.totalUnread > 99 ? '99+' : group.totalUnread}
                  </span>
                )}
              </button>

              {/* Group conversations */}
              {!isCollapsed && group.conversations.map((conv) => (
                <ConversationItem
                  key={conv.id}
                  conv={conv}
                  isSelected={conv.id === selectedId}
                  onSelect={onSelect}
                  compact
                />
              ))}
            </div>
          )
        })}
        <ListFooter
          loaded={conversations.length}
          total={total}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          loadMoreError={loadMoreError}
          onLoadMore={onLoadMore}
        />
      </div>
    )
  }

  // Flat view (default)
  return (
    <div {...tourAnchor('inbox.list.rows')} className="h-full overflow-y-auto">
      {conversations.map((conv) => (
        <ConversationItem
          key={conv.id}
          conv={conv}
          isSelected={conv.id === selectedId}
          onSelect={onSelect}
        />
      ))}
      <ListFooter
        loaded={conversations.length}
        total={total}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        loadMoreError={loadMoreError}
        onLoadMore={onLoadMore}
      />
    </div>
  )
}
